import { useState } from 'react';
import { createCostCategorySchema, type CostCategory } from '@faktorplus/shared';
import {
  useCostCategories,
  useCreateCostCategory,
  useUpdateCostCategory,
} from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { PencilIcon } from '../../components/ui/icons';
import { Field, Input } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import pageStyles from '../../components/layout/page.module.css';
import styles from './settings-page.module.css';

export function CategoriesTab() {
  // Hidden categories must stay listed here, or switching one off would be
  // irreversible — this is the only screen that can switch it back on.
  const { data: categories = [], isLoading, error } = useCostCategories(true);
  const [editing, setEditing] = useState<CostCategory | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać kategorii.</ErrorBox>;

  return (
    <>
      <div className={pageStyles.header}>
        <span className={pageStyles.subtitle}>
          Kategorie, po których filtruje się koszty — odpowiednik kolumny KATEGORIA w Excelu
        </span>
        <div className={pageStyles.actions}>
          <Button variant="primary" onClick={() => setIsCreating(true)}>
            + Nowa kategoria
          </Button>
        </div>
      </div>

      <div className={pageStyles.tableWrap}>
        <table className={pageStyles.table} style={{ minWidth: 480 }}>
          <thead>
            <tr>
              <th>Nazwa</th>
              <th className={pageStyles.numeric}>Kolejność</th>
              <th>Stan</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.id} className={category.isActive ? undefined : styles.inactive}>
                <td className={pageStyles.strong}>{category.name}</td>
                <td className={pageStyles.numeric}>{category.sortOrder}</td>
                <td className={pageStyles.muted}>{category.isActive ? 'Aktywna' : 'Ukryta'}</td>
                <td>
                  <div className={styles.rowActions}>
                    <Button variant="soft" icon={<PencilIcon />} onClick={() => setEditing(category)}>
                      Edytuj
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(isCreating || editing) && (
        <CategoryDialog
          category={editing}
          nextSortOrder={Math.max(0, ...categories.map((category) => category.sortOrder)) + 1}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function CategoryDialog({
  category,
  nextSortOrder,
  onClose,
}: {
  category: CostCategory | null;
  /** Where a new category lands: at the end of the 1, 2, 3 the list is kept in. */
  nextSortOrder: number;
  onClose: () => void;
}) {
  const create = useCreateCostCategory();
  const update = useUpdateCostCategory();

  const [name, setName] = useState(category?.name ?? '');
  const [sortOrder, setSortOrder] = useState(String(category?.sortOrder ?? nextSortOrder));
  const [isActive, setIsActive] = useState(category?.isActive ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isPending = create.isPending || update.isPending;
  const serverError = create.error ?? update.error;

  async function handleSubmit() {
    setValidationError(null);

    const parsed = createCostCategorySchema.safeParse({
      name: name.trim(),
      sortOrder: Number(sortOrder) || nextSortOrder,
    });

    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Sprawdź wprowadzone dane');
      return;
    }

    if (category) {
      await update.mutateAsync({ id: category.id, input: { ...parsed.data, isActive } });
    } else {
      await create.mutateAsync(parsed.data);
    }
    onClose();
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={category ? 'Edytuj kategorię' : 'Nowa kategoria'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button variant="primary" isLoading={isPending} onClick={() => void handleSubmit()}>
            {category ? 'Zapisz zmiany' : 'Dodaj kategorię'}
          </Button>
        </>
      }
    >
      <Field label="Nazwa">
        {(id) => (
          <Input
            id={id}
            value={name}
            autoFocus={!category}
            placeholder="Materiały"
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>

      <Field label="Kolejność na liście" hint="Mniejsza liczba = wyżej na rozwijanej liście.">
        {(id) => (
          <Input
            id={id}
            type="number"
            min="1"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        )}
      </Field>

      {category && (
        <label className={modalStyles.checkItem}>
          <input
            type="checkbox"
            className={pageStyles.checkbox}
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Kategoria aktywna
        </label>
      )}

      {validationError && <ErrorBox>{validationError}</ErrorBox>}
      {serverError && (
        <ErrorBox>
          {serverError instanceof ApiError ? serverError.message : 'Nie udało się zapisać.'}
        </ErrorBox>
      )}
    </Modal>
  );
}
